/**
 * =============================================================================
 * AI SPOTLIGHT CARD COMPONENT - DASHBOARD DESIGN SYSTEM
 * =============================================================================
 *
 * Matches WatchCard/Top Stories typography exactly:
 * - Label: 10px uppercase, muted, letter-spacing
 * - Heading: 15px semibold
 * - Action: 12px muted
 */

import { Box, Text, Group, Skeleton } from '@mantine/core';
import {
    CaretRight,
    Warning,
    Heartbeat,
    Package,
    Timer,
    ChartLineUp,
    TrendUp,
    Cube,
} from '@phosphor-icons/react';
import ContextPills from '@components/common/ContextPills';
import { memo, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router';
import type {
    AISpotlightCardProps,
    AISpotlightCardState,
    AISpotlightCardConfig,
    AISpotlightEvaluatedData,
} from '../../types';
import { getMerchantId, getAuthHeaders, getApiBase } from '../../api';

// =============================================================================
// DESIGN TOKENS - Matching WatchCard exactly
// =============================================================================
const spacing = {
    xs: 8,
} as const;

const semanticColors = {
    critical: 'rgb(205, 101, 101)',
} as const;

// =============================================================================
// ICON MAPPING
// =============================================================================
const iconMap: Record<
    string,
    React.ComponentType<{
        size?: number;
        weight?: 'fill' | 'bold' | 'regular';
        className?: string;
        style?: React.CSSProperties;
    }>
> = {
    heartbeat: Heartbeat,
    package: Package,
    timer: Timer,
    'chart-line-up': ChartLineUp,
    'trend-up': TrendUp,
    cube: Cube,
};

const getIconComponent = (iconName?: string) => {
    if (!iconName) return null;
    return iconMap[iconName] || null;
};

// =============================================================================
// VALID STATUSES FOR FILTERING
// =============================================================================
const DEFAULT_OPEN_STATUSES = ['CREATED', 'SEEN'];

// =============================================================================
// API: Fetch data for spotlight card
// =============================================================================

interface CheckListItemResponse {
    checklistItemId: string;
    merchantId: string;
    heading: string;
    data?: string | null;
    status: string;
    alertId?: string | null;
    alertType?: string | null;
    generatedBy?: string | null;
    [key: string]: unknown;
}

const fetchSpotlightData = async (
    config: AISpotlightCardConfig,
): Promise<AISpotlightCardState> => {
    const { dataSource, fallback } = config;

    try {
        const merchantId = await getMerchantId();

        if (!merchantId) {
            console.error(
                `[AISpotlightCard] Could not resolve merchant_id for card ${config.id}`,
            );

            if (fallback) {
                return {
                    data: {
                        mainContent: fallback.mainContent,
                        secondaryContent: fallback.secondaryContent,
                    },
                    isLoading: false,
                    error: 'Authentication required',
                    lastFetched: null,
                };
            }

            return {
                data: null,
                isLoading: false,
                error: 'Authentication required',
                lastFetched: null,
            };
        }

        const headers = getAuthHeaders();
        const apiBase = getApiBase();

        // Build query params - add generated_by if using alertType (merchant-agnostic)
        const queryParams = new URLSearchParams({
            merchant_id: merchantId,
            ...dataSource.queryParams,
        });

        // Add generated_by filter if specified (for merchant-agnostic filtering)
        if (dataSource.filters?.generatedBy) {
            queryParams.set('generated_by', dataSource.filters.generatedBy);
        }

        const url = `${apiBase}${dataSource.endpoint}?${queryParams.toString()}`;

        console.log(
            `[AISpotlightCard] Fetching data for card: ${config.id}, alertType: ${dataSource.filters?.alertType || 'N/A'}, generatedBy: ${dataSource.filters?.generatedBy || 'N/A'}`,
        );

        const response = await fetch(url, {
            method: dataSource.method,
            headers,
            body:
                dataSource.method === 'POST'
                    ? JSON.stringify(dataSource.bodyParams)
                    : undefined,
        });

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const responseData: CheckListItemResponse[] = await response.json();

        let filteredData = responseData;

        // Filter by alertType (merchant-agnostic) OR alertId (legacy/merchant-specific)
        if (dataSource.filters?.alertType) {
            // NEW: Merchant-agnostic filtering by alertType
            filteredData = filteredData.filter(
                (item) => item.alertType === dataSource.filters?.alertType,
            );
            console.log(
                `[AISpotlightCard] Filtering by alertType=${dataSource.filters.alertType}, found ${filteredData.length} items`,
            );
        } else if (dataSource.filters?.alertId) {
            // LEGACY: Merchant-specific filtering by alertId (deprecated)
            filteredData = filteredData.filter(
                (item) => item.alertId === dataSource.filters?.alertId,
            );
            console.log(
                `[AISpotlightCard] Filtering by alertId=${dataSource.filters.alertId} (deprecated), found ${filteredData.length} items`,
            );
        }

        // Filter by status
        if (
            dataSource.filters?.statuses &&
            dataSource.filters.statuses.length > 0
        ) {
            filteredData = filteredData.filter((item) =>
                dataSource.filters?.statuses?.includes(item.status),
            );
        } else {
            filteredData = filteredData.filter((item) =>
                DEFAULT_OPEN_STATUSES.includes(item.status),
            );
        }

        const matchingItem = filteredData[0];

        if (!matchingItem) {
            console.log(
                `[AISpotlightCard] No matching item found for card: ${config.id}`,
            );

            if (fallback) {
                return {
                    data: {
                        mainContent: fallback.mainContent,
                        secondaryContent: fallback.secondaryContent,
                    },
                    isLoading: false,
                    error: null,
                    lastFetched: new Date().toISOString(),
                };
            }

            return {
                data: null,
                isLoading: false,
                error: 'No data found',
                lastFetched: new Date().toISOString(),
            };
        }

        const mainContent = String(
            matchingItem[dataSource.mainContentKey] || '',
        );
        const secondaryContent = dataSource.secondaryContentKey
            ? String(matchingItem[dataSource.secondaryContentKey] || '')
            : undefined;

        // Extract context filters from data.filters.a.sourceParams (same logic as WatchCard)
        let contextFilters: Record<string, unknown> | undefined;
        const dataJson = matchingItem.data as string | null | undefined;
        if (dataJson) {
            try {
                const parsed = JSON.parse(dataJson);
                const raw =
                    parsed?.filters?.a?.sourceParams ?? parsed?.filters?.a;
                if (raw && typeof raw === 'object') {
                    contextFilters = raw as Record<string, unknown>;
                }
            } catch {
                // ignore
            }
        }

        const evaluatedData: AISpotlightEvaluatedData = {
            mainContent,
            secondaryContent,
            checklistItemId: matchingItem.checklistItemId,
            rawData: matchingItem,
            contextFilters,
        };

        console.log(
            `[AISpotlightCard] Found data for card ${config.id}: ${mainContent}`,
        );

        return {
            data: evaluatedData,
            isLoading: false,
            error: null,
            lastFetched: new Date().toISOString(),
        };
    } catch (error) {
        console.error(
            `[AISpotlightCard] Error fetching data for card ${config.id}:`,
            error,
        );

        if (fallback) {
            return {
                data: {
                    mainContent: fallback.mainContent,
                    secondaryContent: fallback.secondaryContent,
                },
                isLoading: false,
                error:
                    error instanceof Error
                        ? error.message
                        : 'Failed to fetch data',
                lastFetched: null,
            };
        }

        return {
            data: null,
            isLoading: false,
            error:
                error instanceof Error ? error.message : 'Failed to fetch data',
            lastFetched: null,
        };
    }
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================
const AISpotlightCard: React.FC<AISpotlightCardProps> = memo(
    ({
        config,
        data: controlledData,
        isLoading: controlledLoading,
        error: controlledError,
        className = '',
        onClick,
    }) => {
        const navigate = useNavigate();

        const [state, setState] = useState<AISpotlightCardState>({
            data: null,
            isLoading: true,
            error: null,
            lastFetched: null,
        });

        const isControlled = controlledData !== undefined;

        const displayData = isControlled ? controlledData : state.data;
        const displayLoading =
            controlledLoading ?? (!isControlled && state.isLoading);
        const displayError = controlledError ?? state.error;

        useEffect(() => {
            if (!isControlled) {
                const loadData = async () => {
                    setState((prev) => ({
                        ...prev,
                        isLoading: true,
                        error: null,
                    }));
                    const result = await fetchSpotlightData(config);
                    setState(result);
                };

                loadData();
            }
        }, [config, isControlled]);

        const handleClick = useCallback(() => {
            if (onClick) {
                onClick();
            } else if (displayData?.checklistItemId) {
                // Use checklistItemId from response for navigation
                const path = config.drilldownPath.replace(
                    ':alertId',
                    displayData.checklistItemId,
                );
                navigate(path);
            }
            // If no checklistItemId, clicking does nothing
        }, [onClick, navigate, config.drilldownPath, displayData]);

        const IconComponent = getIconComponent(config.icon);

        return (
            <Box
                className={`group cursor-pointer ${className}`}
                style={{ flex: 1, minWidth: 0 }}
                onClick={handleClick}
            >
                {displayLoading ? (
                    <Box
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 4,
                        }}
                    >
                        <Skeleton height={10} width="50%" radius={2} />
                        <Skeleton height={18} width="80%" radius={2} />
                        <Skeleton height={12} width="35%" radius={2} />
                    </Box>
                ) : displayError && !displayData ? (
                    <Group
                        style={{
                            gap: spacing.xs,
                            color: semanticColors.critical,
                        }}
                    >
                        <Warning size={14} weight="fill" />
                        <Text style={{ fontSize: 12 }}>Failed to load</Text>
                    </Group>
                ) : (
                    <Box
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 4,
                        }}
                    >
                        {/* Label: 10px uppercase muted (matches WatchCard category) */}
                        <Group style={{ gap: 4 }} wrap="nowrap">
                            {IconComponent && (
                                <IconComponent
                                    size={12}
                                    weight="fill"
                                    style={{
                                        color: 'rgb(148, 163, 184)',
                                        flexShrink: 0,
                                    }}
                                />
                            )}
                            <Text
                                style={{
                                    fontSize: 10,
                                    fontWeight: 600,
                                    letterSpacing: '0.05em',
                                    textTransform: 'uppercase',
                                    color: 'rgb(148, 163, 184)',
                                }}
                            >
                                {config.summary}
                            </Text>
                        </Group>

                        {/* Heading: 15px semibold (matches WatchCard heading) */}
                        <Text
                            style={{
                                fontSize: 15,
                                fontWeight: 600,
                                lineHeight: 1.3,
                                color: 'rgb(26, 32, 44)',
                            }}
                            className="line-clamp-2"
                        >
                            {displayData?.mainContent ||
                                config.fallback?.mainContent ||
                                '—'}
                        </Text>

                        {/* Context pills — show filters if present */}
                        {displayData?.contextFilters && (
                            <ContextPills
                                filters={displayData.contextFilters}
                            />
                        )}

                        {/* Action: 12px muted (matches WatchCard action hint) */}
                        <Group
                            style={{ gap: 4, marginTop: 4 }}
                            className="opacity-60 transition-opacity group-hover:opacity-100"
                            wrap="nowrap"
                        >
                            <Text
                                style={{
                                    fontSize: 12,
                                    fontWeight: 500,
                                    color: 'rgb(100, 116, 139)',
                                }}
                            >
                                Know more
                            </Text>
                            <CaretRight
                                size={12}
                                weight="bold"
                                style={{ color: 'rgb(148, 163, 184)' }}
                            />
                        </Group>
                    </Box>
                )}
            </Box>
        );
    },
);

AISpotlightCard.displayName = 'AISpotlightCard';

export default AISpotlightCard;
